import { MESSAGES as BASE_MESSAGES } from '@octbill/shared/messages';
import { createElement, Fragment } from 'react';

import { useAuth } from '../auth.jsx';

/**
 * Builds t(id, vars) over the message catalogue with the person's language and the admin's wording
 * overrides (both fetched by auth.jsx from GET /api/public/messages/auto). `{name}` placeholders come
 * from `vars`. English (shared/src/messages.js) fills every gap.
 *
 * Plurals: when `vars.count` is a number and the catalogue has `<id>.one` / `<id>.other`, t() picks
 * `.one` for 1 and `.other` for everything else.
 */
export function makeT(overrides = {}, extra = {}) {
  const has = (id) => id in overrides || id in extra || id in BASE_MESSAGES;
  const lookup = (id) => overrides[id] ?? extra[id] ?? BASE_MESSAGES[id] ?? id;
  return function t(id, vars = {}) {
    let key = id;
    if (typeof vars.count === 'number') {
      const form = `${id}.${vars.count === 1 ? 'one' : 'other'}`;
      if (has(form)) key = form;
    }
    return lookup(key).replace(/\{(\w+)\}/g, (match, name) => (name in vars ? String(vars[name]) : match));
  };
}

/**
 * t(id) for ids built from data (`domain_status.${status}`): `fallback` (usually the raw value) when
 * the catalogue has no such id, instead of the id itself.
 */
export function tOr(t, id, fallback, vars) {
  const text = t(id, vars);
  return text === id ? fallback : text;
}

/**
 * Puts elements into a translated text: `fill(t('x'), { file: <span>…</span> })` replaces the
 * `{file}` that t() left in place.
 */
export function fill(text, parts) {
  return text
    .split(/\{(\w+)\}/g)
    .map((piece, i) =>
      i % 2 === 0 ? piece : piece in parts ? createElement(Fragment, { key: i }, parts[piece]) : `{${piece}}`,
    );
}

export function useT() {
  return makeT(useAuth()?.messages);
}

/** t() with a part of the catalogue that loads with a lazy chunk. */
export function useTWith(extra) {
  return makeT(useAuth()?.messages, extra);
}

// The locale of the language on screen ('de-DE'), for Intl formatting in plain helpers outside
// components (table cells). auth.jsx sets it when the catalogue arrives.
let currentLocale = 'en';

export function setLocale(locale) {
  currentLocale = locale || 'en';
}

export const getLocale = () => currentLocale;

/** The locale for Intl in components: follows the person's language. */
export function useLocale() {
  return useAuth()?.locale ?? currentLocale;
}

/** Numbers in the person's language ("1.234,5" in German). */
export function formatNumber(value, options = {}, locale = currentLocale) {
  return new Intl.NumberFormat(locale, options).format(value);
}

/** A date in the person's language; `style` is an Intl dateStyle ('short' | 'medium' | 'long'). */
export function formatLocalDate(value, style = 'medium', locale = currentLocale) {
  if (!value) return '';
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(String(value)) ? `${value}T00:00:00Z` : value);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(locale, {
    dateStyle: style,
    ...(/^\d{4}-\d{2}-\d{2}$/.test(String(value)) ? { timeZone: 'UTC' } : {}),
  }).format(d);
}
